import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { format, formatDistanceToNow } from 'date-fns'
import { useAuth } from '../context/AuthContext'
import api, { getReconciliationStatus } from '../services/api'
import { parseDateOnly } from '../utils/format'
import { toFirmWallClock } from '../utils/eventTime'
import { firmDueDay, isDueAfterStartDay } from '../utils/todoDue'
import useFirmTimezone from '../hooks/useFirmTimezone'
import PopOutPageButton from '../components/PopOutPageButton'
import HelpButton from '../components/HelpButton'
import { SkeletonMetric, SkeletonCard, LoadingRegion } from '../components/Skeleton'
import useTimerEntrySaved from '../hooks/useTimerEntrySaved'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import FirmDashboardTab from '../components/FirmDashboardTab'
import { useChartSizing } from '../hooks/useDisplayZoom'
import FirmFeedTab from '../components/FirmFeedTab'
import { followupLabel, followupChipClass, followupIcon } from '../lib/leadFollowup'

const ICON = {
  time_entry:    '⏱',
  invoice_paid:  '💰',
  client_new:    '👤',
  trust_deposit: '🏦',
  matter_new:    '📁'
}

// Dates on this page are the FIRM's, matching the Calendar and the Daily
// Snapshot (James, 2026-10-17; Dashboard.firmDay.test.js):
//   - an event's start_time is a naive-UTC instant — shown on the firm's wall
//     clock (toFirmWallClock), never the browser's, so "Upcoming This Week"
//     names the same day and time the Calendar shows;
//   - sol_date, due_date, last_entry and last_activity are DATE values (node-pg
//     sends UTC midnight) — parseDateOnly, never new Date(), which showed them
//     a day early in US zones;
//   - the date under the title is today on the firm's clock.
// Each returns '' for a missing or invalid value (date-fns format throws on
// an Invalid Date, and one bad row must not take the page down).
const fmtValid = (d, pattern) => (d instanceof Date && !isNaN(d.getTime()) ? format(d, pattern) : '')
const fmtFirmClock = (value, pattern) => (value ? fmtValid(toFirmWallClock(value), pattern) : '')
const fmtDateOnly = (value, pattern) => (value ? fmtValid(parseDateOnly(value), pattern) : '')

// "Upcoming This Week" line: the firm's day and time it starts — except an
// Amicus deadline to-do due on a later day than it starts, which the API
// lists by its DUE day (eventDueAfterStartDaySql) and which shows "Due <day>",
// no time (James 2026-10-21, "due date everywhere"; utils/todoDue.js).
export function upcomingWhen (e) {
  if (isDueAfterStartDay(e)) {
    const day = fmtValid(firmDueDay(e), 'EEE MMM d')
    return day ? `Due ${day}` : ''
  }
  return fmtFirmClock(e && e.start_time, 'EEE MMM d, h:mm a')
}

export default function Dashboard() {
  const { user } = useAuth()
  // The firm zone for the dates and times below — fetched when the Dashboard
  // is popped out (PopupLayout never sets it), re-rendering once it is known.
  useFirmTimezone()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Chart px (Recharts) follow the automatic zoom like the rest of the page.
  const chart = useChartSizing()
  const isAdmin = user?.role === 'admin' || user?.permissions?.is_admin
  const canViewAllTime = isAdmin || user?.permissions?.can_view_all_time === true
  const canViewFirmFinancials = isAdmin || user?.permissions?.can_view_firm_financials === true
  // Ship 2 — Firm Dashboard tab is visible when the user has admin OR
  // can_view_all_time OR can_view_firm_financials.  Firm Feed tab is visible
  // to every staff member (backend scopes to the caller's visibility).
  const canSeeFirmDashboard = isAdmin || canViewAllTime || canViewFirmFinancials
  const [dashboardTab, setDashboardTab] = useState(() => {
    try {
      const stored = localStorage.getItem('lextrack_dashboard_tab')
      if (stored === 'firm' || stored === 'feed' || stored === 'my') return stored
    } catch {}
    return 'my'
  })
  const switchTab = (tab) => {
    // Guard against a stored 'firm' selection surviving a perm downgrade.
    const next = tab === 'firm' && !canSeeFirmDashboard ? 'my' : tab
    setDashboardTab(next)
    try { localStorage.setItem('lextrack_dashboard_tab', next) } catch {}
  }
  // Repair persisted tab on mount if perms shrank since last visit.
  useEffect(() => {
    if (dashboardTab === 'firm' && !canSeeFirmDashboard) switchTab('my')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeFirmDashboard])
  // ?scope=mine toggle for admins/can_view_all_time users — flips the page to
  // "personal view" without demoting permissions.  Persisted in localStorage.
  const [scopeMine, setScopeMine] = useState(() => {
    try { return localStorage.getItem('lextrack_dashboard_scope') === 'mine' } catch { return false }
  })

  // Per-user Dashboard customization — hide widgets you don't want to see.
  // Backend permission-gating still applies; this is an additional user
  // preference layer on top ("I have permission but don't want it here").
  // Persist as a Set of widget IDs in localStorage.
  // Per-widget permission gates — only offer toggles for widgets the user
  // can actually see.  Backend still returns null for ungated fields; this
  // just keeps the customize panel from cluttering with unreachable options.
  const perms = user?.permissions || {}
  const canSee = (p) => isAdmin || perms[p] === true
  const DASHBOARD_WIDGETS = [
    { id: 'month_revenue',           label: 'This Month Revenue card',        visible: canSee('invoices') },
    { id: 'wip',                     label: 'WIP hours card',                 visible: canSee('time_entries') },
    { id: 'outstanding_ar',          label: 'Outstanding A/R card',           visible: canSee('invoices') },
    { id: 'trust_total',             label: 'Trust Balances card',            visible: canSee('trust') },
    { id: 'monthly_revenue_chart',   label: 'Monthly Revenue chart',          visible: canSee('invoices') },
    { id: 'hours_by_attorney_chart', label: 'Hours by Attorney chart',        visible: canSee('time_entries') },
    { id: 'recent_activity',         label: 'Recent Activity feed',           visible: true },
    { id: 'upcoming_events',         label: 'Upcoming This Week',             visible: canSee('calendar') || canSee('tasks') },
    { id: 'quick_actions',           label: 'Quick Actions row',              visible: true },
    { id: 'alerts',                  label: 'Alerts row (SOL / overdue / etc.)', visible: true },
    { id: 'unreturned_callbacks',    label: 'Unreturned Messages card',       visible: canSee('communications') },
    { id: 'financial_health',        label: 'AR & Collections Health card',   visible: canSee('invoices') },
    { id: 'security_alerts',         label: 'Security Alerts (admin only)',   visible: isAdmin },
    // Ship 7j — Phone-intake pending-review card (feedback #743).
    // Visible only to admin OR the firm's designated reviewer (server
    // enforces the gate — endpoint returns 403 for anyone else so the
    // card renders "no queue" quietly for staff who aren't the reviewer).
    { id: 'phone_intake_pending',    label: 'Phone Intake Pending Review card', visible: canSee('leads') || isAdmin },
    // Ledger Balance — the Dashboard half of the reconciliation pulse.  The
    // permission key is `can_view_accounting`, NOT `accounting`: there is no
    // plain `accounting` entry in KNOWN_PERMS (Layout's nav item gates on
    // can_view_accounting too), so spelling it the short way would silently
    // narrow this card to admins and hide it from the bookkeeper it is for.
    { id: 'ledger_balance',          label: 'Ledger Balance card',            visible: canSee('can_view_accounting') },
    // True Profit This Month — gated on `can_view_cost_rates`, matching the
    // server.  NOT `time_entries`: attorney cost rate is salary-adjacent data
    // and rides its own HR gate, so offering the toggle to every time-entry
    // holder would put a switch in the Customize panel for a card the backend
    // will never send them — the same mismatch the Ledger Balance note above
    // warns about, one key in the other direction.
    { id: 'true_profit_mtd',         label: 'True Profit This Month card',    visible: canSee('can_view_cost_rates') }
  ]
  const [hiddenWidgets, setHiddenWidgets] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('lextrack_dashboard_hidden') || '[]')) } catch { return new Set() }
  })
  const [showCustomize, setShowCustomize] = useState(false)
  const isWidgetVisible = (id) => !hiddenWidgets.has(id)
  const toggleWidget = (id) => {
    setHiddenWidgets((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      try { localStorage.setItem('lextrack_dashboard_hidden', JSON.stringify([...next])) } catch {}
      return next
    })
  }
  const resetLayout = () => {
    setHiddenWidgets(new Set())
    try { localStorage.setItem('lextrack_dashboard_hidden', '[]') } catch {}
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on scope change only; fetchData is re-created every render
  useEffect(() => { fetchData() }, [scopeMine])

  // Re-fetch summary tiles whenever a timer save happens in this window OR
  // in a pop-out timer window (cross-window broadcast via storage event).
  useTimerEntrySaved(() => fetchData())
  const fetchData = async () => {
    setLoading(true); setError('')
    try {
      const url = scopeMine && canViewAllTime ? '/api/dashboard?scope=mine' : '/api/dashboard'
      const res = await api.get(url)
      setData(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading dashboard')
    } finally { setLoading(false) }
  }
  const toggleScope = () => {
    const next = !scopeMine
    setScopeMine(next)
    try { localStorage.setItem('lextrack_dashboard_scope', next ? 'mine' : 'all') } catch {}
  }

  // Page-scoped keyboard shortcuts: r = refresh dashboard, c = customize panel
  usePageShortcut('r', () => fetchData(), { enabled: true })
  usePageShortcut('c', () => setShowCustomize(v => !v), { enabled: true })

  // Ship 2 — tab bar rendered above every dashboard variant.  Kept as an
  // inline renderer so the same JSX is used in loading / error / firm / feed
  // paths without prop-drilling.
  const renderTabBar = () => (
    <div className="flex gap-2 mb-4 items-center border-b border-gray-200 -mt-1 pb-0">
      <button
        onClick={() => switchTab('my')}
        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
          dashboardTab === 'my'
            ? 'border-primary-600 text-primary-700'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        🏠 My Dashboard
      </button>
      {canSeeFirmDashboard && (
        <button
          onClick={() => switchTab('firm')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
            dashboardTab === 'firm'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 Firm Dashboard
        </button>
      )}
      <button
        onClick={() => switchTab('feed')}
        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
          dashboardTab === 'feed'
            ? 'border-primary-600 text-primary-700'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        🌊 Firm Feed
      </button>
    </div>
  )

  // Firm dashboard / feed tabs are independent of the /api/dashboard fetch,
  // so short-circuit before the loading gate — otherwise switching tabs while
  // a refresh is in flight briefly renders the skeleton in the wrong tab.
  if (dashboardTab === 'firm' && canSeeFirmDashboard) {
    // The Firm tab's Realization and Collection figures come from the same
    // imported invoices and payments, so it carries the same caption when the
    // server sent one (GET /api/dashboard → figures_source; null for callers
    // who get no money figures and once billing is recorded in Omnia).
    const firmCaption = typeof data?.figures_source?.caption === 'string' ? data.figures_source.caption : null
    return (
      <div>
        {renderTabBar()}
        {firmCaption && (
          <p className="mb-3 text-xs text-gray-500" data-testid="figures-source-caption-firm">
            <span aria-hidden="true">ⓘ </span>{firmCaption}
          </p>
        )}
        <FirmDashboardTab user={user} />
      </div>
    )
  }
  if (dashboardTab === 'feed') {
    return (
      <div>
        {renderTabBar()}
        <FirmFeedTab user={user} />
      </div>
    )
  }

  if (loading) return (
    <div>
      {renderTabBar()}
      <LoadingRegion label="Loading dashboard">
        <div className="mb-6">
          <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SkeletonMetric /><SkeletonMetric /><SkeletonMetric /><SkeletonMetric />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <SkeletonCard height={280} /><SkeletonCard height={280} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <SkeletonCard height={200} /><SkeletonCard height={200} />
        </div>
      </LoadingRegion>
    </div>
  )
  if (error) return (
    <div>
      {renderTabBar()}
      <div className="bg-red-50 text-red-600 rounded-lg p-4">{error}</div>
    </div>
  )
  if (!data) return (
    <div>
      {renderTabBar()}
    </div>
  )

  // Default every top-level field to safe shapes so a partial API response
  // (permission-gated user, upstream degraded state) doesn't crash the
  // whole Dashboard.  Backend already returns null/[] for permission
  // gates; this is belt-and-suspenders.
  const {
    metrics = {},
    monthly_revenue = [],
    hours_by_attorney = [],
    recent_activity = [],
    upcoming_events = [],
    alerts = {},
    figures_source = null
  } = data || {}

  // "Billing and trust figures come from the Amicus import of <date>."
  // The server decides whether it applies and writes the sentence and the
  // firm-zone date(s) (GET /api/dashboard → figures_source; null once anyone
  // records an invoice, payment or trust entry in Omnia, and for callers who
  // get no money tiles).  Shown only while at least one of the figures it
  // describes is actually on screen — the four money tiles, the monthly
  // revenue chart, or the AR & Collections Health card (same invoice data,
  // same server gate) — not for a user who hid them all.
  const moneyFiguresShown = !!(
    (isWidgetVisible('month_revenue') && metrics.month_revenue) ||
    (isWidgetVisible('outstanding_ar') && metrics.outstanding_ar) ||
    (isWidgetVisible('trust_total') && metrics.trust_total) ||
    (isWidgetVisible('monthly_revenue_chart') && monthly_revenue.length > 0) ||
    (isWidgetVisible('financial_health') && alerts.financial_health)
  )
  const figuresCaption = moneyFiguresShown && figures_source && typeof figures_source.caption === 'string'
    ? figures_source.caption
    : null

  return (
    <div>
      {renderTabBar()}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            Dashboard
            {isAdmin && <HealthDot />}
            <HelpButton slug="dashboard" />
            <PopOutPageButton title="Dashboard" width={1400} height={900} />
          </h2>
          <p className="text-sm text-gray-500">{fmtFirmClock(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canViewAllTime && (
            <button onClick={toggleScope}
              disabled={loading}
              title={scopeMine ? 'Showing only your data — click to view firm-wide' : 'Showing firm-wide — click to view your data only'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                scopeMine
                  ? 'bg-purple-600 border-purple-600 text-white hover:bg-purple-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              } ${loading ? 'opacity-60 cursor-wait' : ''}`}>
              {loading ? '⟳ Loading…' : (scopeMine ? '👤 My View' : '👥 Firm-Wide View')}
            </button>
          )}
          {/* Customize — small ⚙️ button opens a checkbox popover so users
              can hide widgets they don't want to see.  Backend permission
              gating still applies; this is an additional user pref. */}
          <div className="relative">
            <button
              onClick={() => setShowCustomize(v => !v)}
              title="Customize Dashboard — hide/show widgets"
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors">
              ⚙️
            </button>
            {showCustomize && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowCustomize(false)} />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-lg border border-gray-200 p-4 z-30">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-800">Customize Dashboard</h3>
                    <button type="button" onClick={() => setShowCustomize(false)}
                      className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Uncheck to hide a widget.  Only affects your view — other users see whatever they've configured.
                  </p>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {DASHBOARD_WIDGETS.filter((w) => w.visible).map((w) => (
                      <label key={w.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                        <input type="checkbox"
                          checked={!hiddenWidgets.has(w.id)}
                          onChange={() => toggleWidget(w.id)} />
                        <span className="flex-1 text-gray-700">{w.label}</span>
                      </label>
                    ))}
                  </div>
                  {hiddenWidgets.size > 0 && (
                    <div className="pt-2 border-t border-gray-100 mt-2">
                      <button type="button" onClick={resetLayout}
                        className="w-full text-xs text-primary-600 hover:text-primary-800 hover:underline">
                        Reset — show everything
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Row 1 — Key Metrics (each conditionally rendered: backend returns
          null for metrics the user doesn't have permission to see; user
          can additionally hide any card via the Customize popover) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {isWidgetVisible('month_revenue') && metrics.month_revenue && (
          <MetricCard
            icon="💰"
            title="This Month Revenue"
            value={`$${fmt(metrics.month_revenue.amount)}`}
            trend={metrics.month_revenue.trend}
            change={metrics.month_revenue.change_percent}
            color="bg-indigo-50 text-indigo-700"
            link="/reports"
          />
        )}
        {isWidgetVisible('wip') && metrics.wip && (
          <MetricCard
            icon="⏱"
            title="WIP"
            value={`${metrics.wip.week_hours.toFixed(1)} hrs this week`}
            subvalue={`${metrics.wip.today_hours.toFixed(1)} hrs today · Mon–Sun`}
            color="bg-yellow-50 text-yellow-700"
            link="/billing"
          />
        )}
        {isWidgetVisible('outstanding_ar') && metrics.outstanding_ar && (
          <MetricCard
            icon="📄"
            title="Outstanding A/R"
            value={`$${fmt(metrics.outstanding_ar.amount)}`}
            subvalue={`${metrics.outstanding_ar.count} invoice${metrics.outstanding_ar.count === 1 ? '' : 's'}`}
            color="bg-red-50 text-red-700"
            link="/invoices"
          />
        )}
        {isWidgetVisible('trust_total') && metrics.trust_total && (
          <MetricCard
            icon="🏦"
            title="Trust Balances"
            value={`$${fmt(metrics.trust_total.amount)}`}
            color="bg-green-50 text-green-700"
            link="/trust"
          />
        )}
      </div>
      {figuresCaption && (
        <p className="-mt-4 mb-6 text-xs text-gray-500" data-testid="figures-source-caption">
          <span aria-hidden="true">ⓘ </span>{figuresCaption}
        </p>
      )}

      {/* Row 2 — Charts.  Each chart card hidden when its data array is
          empty (backend returns [] when the user lacks the gating permission)
          or when the user has hidden the widget via Customize. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {isWidgetVisible('monthly_revenue_chart') && monthly_revenue.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">📊 Monthly Revenue (last 6 months)</h3>
            <ResponsiveContainer width="100%" height={chart.height(260)}>
              <BarChart data={monthly_revenue}>
                <XAxis dataKey="month" fontSize={chart.font} height={chart.xAxisHeight} tickLine={false} axisLine={false} />
                <YAxis fontSize={chart.font} width={chart.yAxisWidth} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `$${fmt(v)}`} />
                <Legend wrapperStyle={{ fontSize: chart.font }} />
                <Bar dataKey="billed" fill="#7c3aed" name="Billed" radius={[4, 4, 0, 0]} />
                <Bar dataKey="collected" fill="#10b981" name="Collected" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {isWidgetVisible('hours_by_attorney_chart') && hours_by_attorney.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">📈 Hours by Attorney (this month)</h3>
            <ResponsiveContainer width="100%" height={chart.height(260)}>
              <BarChart data={hours_by_attorney}>
                <XAxis dataKey="name" fontSize={chart.font} height={chart.xAxisHeight} tickLine={false} axisLine={false} />
                <YAxis fontSize={chart.font} width={chart.yAxisWidth} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: chart.font }} />
                <Bar dataKey="billable" stackId="a" fill="#7c3aed" name="Billable" radius={[0, 0, 0, 0]} />
                <Bar dataKey="non_billable" stackId="a" fill="#cbd5e1" name="Non-Billable" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 3 — Activity + Upcoming */}
      {(isWidgetVisible('recent_activity') || isWidgetVisible('upcoming_events')) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {isWidgetVisible('recent_activity') && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">🔔 Recent Activity</h3>
            <div className="flex items-center gap-3">
              <Link to="/activity-feed" className="text-xs text-primary-600 hover:underline">Full feed →</Link>
              <button onClick={fetchData} disabled={loading}
                className="text-xs text-primary-600 hover:underline disabled:opacity-50 disabled:cursor-wait">
                {loading ? '⟳ Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recent_activity.length === 0 && <p className="p-5 text-sm text-gray-500 italic">No recent activity in the last 30 days.</p>}
            {recent_activity.map((a, i) => (
              <button key={i} onClick={() => a.url && navigate(a.url)}
                className="w-full text-left flex items-start gap-3 px-5 py-3 hover:bg-gray-50">
                <span className="text-lg shrink-0">{ICON[a.type] || '•'}</span>
                <span className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate" title={a.description}>{a.description}</p>
                  <p className="text-xs text-gray-400">{a.when ? formatDistanceToNow(new Date(a.when), { addSuffix: true }) : ''}</p>
                </span>
              </button>
            ))}
          </div>
        </div>
        )}

        {isWidgetVisible('upcoming_events') && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">📅 Upcoming This Week</h3>
            <Link to="/calendar" className="text-xs text-primary-600 hover:underline">View Calendar</Link>
          </div>
          <div className="divide-y divide-gray-100">
            {upcoming_events.length === 0 && <p className="p-5 text-sm text-gray-400">Nothing scheduled in the next 7 days.</p>}
            {upcoming_events.map(e => (
              <Link to="/calendar" key={e.id} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50">
                <PriorityDot p={e.priority} />
                <span className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{e.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {upcomingWhen(e)}
                    {e.matter_title ? ` · ${e.matter_title}` : ''}
                  </p>
                </span>
              </Link>
            ))}
          </div>
        </div>
        )}
      </div>
      )}

      {/* Row 4 — Quick Actions */}
      {isWidgetVisible('quick_actions') && (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">⚡ Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* One click, no form (firm decision 2026-10-13): Layout starts a
              timer with no matter — picked later from ⏱ Timers.  Without
              can_post_time Layout says so and starts nothing. */}
          <QuickAction icon="⏱" label="Start Timer" onClick={() => window.dispatchEvent(new CustomEvent('start-timer', { detail: {} }))} />
          {/* Quick-win #3 — pass ?new=1 so the target page auto-opens its
              create form on arrival.  Matches the existing pattern in
              Matters/Clients/Calendar useEffect handlers. */}
          <QuickAction icon="👤" label="New Client" onClick={() => navigate('/clients?new=1')} />
          <QuickAction icon="📁" label="New Matter" onClick={() => navigate('/matters?new=1')} />
          {/* Billing is a matter-picker (batch invoicing flow), not a form —
              landing on the list is correct here. */}
          <QuickAction icon="📄" label="New Invoice" onClick={() => navigate('/billing')} />
          {/* Trust Deposit Quick Action — only shown to users with trust visibility.
              Otherwise the button bait-and-switches: route is gated, so click would 403. */}
          {(user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.trust === true) && (
            <QuickAction icon="🏦" label="Trust Deposit" onClick={() => navigate('/trust?deposit=1')} />
          )}
          <QuickAction icon="📅" label="New Event" onClick={() => navigate('/calendar?new=1')} />
        </div>
      </div>
      )}

      {/* Row 4.5 — Security Alerts (admin only, hidden when zero alerts) */}
      {isAdmin && isWidgetVisible('security_alerts') && (
        <SecurityAlertsCard />
      )}

      {/* Row 5 — Alerts (hide entire row when nothing to alert on — congrats,
          you're caught up!) */}
      {isWidgetVisible('alerts') && [alerts.sol_deadlines, alerts.overdue_invoices, alerts.inactive_matters, alerts.overdue_tasks, alerts.close_out_candidates]
        .some(a => Array.isArray(a) && a.length > 0) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AlertCard title="⚠️ SOL Deadlines (next 90 days)" items={alerts.sol_deadlines} to="/sol" render={(s) => (
          <>
            <p className="text-sm font-medium">{s.cause_of_action}</p>
            <p className="text-xs text-gray-500">{s.matter_title} · {s.days_remaining} days ({fmtDateOnly(s.sol_date, 'MMM d, yyyy')})</p>
          </>
        )} emptyMessage="No SOL deadlines within 90 days." />

        <AlertCard title="🔴 Overdue Invoices (30+ days)" items={alerts.overdue_invoices} to="/invoices" render={(i) => (
          <>
            <p className="text-sm font-medium">Invoice #{i.invoice_number} — ${fmt(i.total)}</p>
            <p className="text-xs text-gray-500">{i.matter_title} · opened {i.created_at && format(new Date(i.created_at), 'MMM d, yyyy')}</p>
          </>
        )} emptyMessage="No overdue invoices." />

        <AlertCard title="⏳ Inactive Matters (no time in 30+ days)" items={alerts.inactive_matters} to="/matters" render={(m) => (
          <>
            <p className="text-sm font-medium">{m.title}</p>
            <p className="text-xs text-gray-500">Last entry: {fmtDateOnly(m.last_entry, 'MMM d, yyyy') || 'Never'}</p>
          </>
        )} emptyMessage="All matters have recent activity." />

        <AlertCard title="📋 Overdue Tasks" items={alerts.overdue_tasks} to="/tasks" render={(t) => (
          <>
            <p className="text-sm font-medium">{t.title}</p>
            <p className="text-xs text-gray-500">{t.matter_title} · due {fmtDateOnly(t.due_date || t.due_day, 'MMM d, yyyy')}</p>
          </>
        )} emptyMessage="No overdue tasks." />

        {/* Close-out candidates — matters with no activity across ANY source
            (time / events / notes / comms / invoices) for 6+ months.  Every
            firm has a handful of these accumulating; surfaced so attorneys
            can either resume the matter or close it. */}
        <AlertCard title="🗄️ Close-Out Candidates (no activity in 6+ months)" items={alerts.close_out_candidates} to="/matters" render={(m) => (
          <>
            <p className="text-sm font-medium">{m.title}</p>
            <p className="text-xs text-gray-500">
              Last activity: {fmtDateOnly(m.last_activity, 'MMM d, yyyy') || 'Never'} · {m.days_stale ? `${m.days_stale} days stale` : 'no recorded activity'}
            </p>
          </>
        )} emptyMessage="No stale matters." />
      </div>
      )}

      {/* Trust compliance alert — only shown when user has trust visibility.
          Surfaces missing monthly reconciliations + active disputes + CTAPP
          deadline countdown so accounting-critical issues don't get buried. */}
      {alerts.trust_compliance && (
        <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-purple-500">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span>🏛️ Trust Compliance</span>
            <Link to="/trust" className="text-xs text-purple-600 hover:underline ml-auto">View Trust →</Link>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className={`rounded-lg p-3 ${alerts.trust_compliance.recon_missing_this_month > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
              <p className="text-xs font-medium text-gray-600 uppercase">Recon this month</p>
              <p className={`text-lg font-bold ${alerts.trust_compliance.recon_missing_this_month > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {alerts.trust_compliance.recon_missing_this_month > 0
                  ? `${alerts.trust_compliance.recon_missing_this_month} missing`
                  : '✓ Done'}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${alerts.trust_compliance.active_disputes > 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
              <p className="text-xs font-medium text-gray-600 uppercase">Disputed</p>
              <p className={`text-lg font-bold ${alerts.trust_compliance.active_disputes > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>
                {alerts.trust_compliance.active_disputes}
              </p>
            </div>
            <div className={`rounded-lg p-3 ${alerts.trust_compliance.ctapp_days_until_due <= 30 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
              <p className="text-xs font-medium text-gray-600 uppercase">CTAPP due</p>
              <p className={`text-lg font-bold ${alerts.trust_compliance.ctapp_days_until_due <= 30 ? 'text-yellow-700' : 'text-gray-600'}`}>
                {alerts.trust_compliance.ctapp_days_until_due} days
              </p>
            </div>
            <div className="rounded-lg p-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-600 uppercase">Active accounts</p>
              <p className="text-lg font-bold text-gray-700">{alerts.trust_compliance.active_accounts}</p>
            </div>
          </div>
        </div>
      )}

      {/* Batch D-D.2: Financial health KPI card.  3-cell grid: Stale WIP,
          DSO trend, Expected 30d collections.  Only rendered when caller
          has `invoices` perm AND the widget is enabled AND the payload
          arrived (backend gates it behind canSeeFinancials). */}
      {alerts.financial_health && isWidgetVisible('financial_health') && (
        <FinancialHealthCard data={alerts.financial_health} />
      )}

      {/* True Profit This Month.  Both guards stay at the CALL SITE, where
          every sibling card keeps them: the payload guard (backend withholds
          it without `can_view_cost_rates`) and the Customize-panel guard. */}
      {alerts.true_profit_mtd && isWidgetVisible('true_profit_mtd') && (
        <TrueProfitCard data={alerts.true_profit_mtd} />
      )}

      {/* Batch D-B: Unreturned callbacks alert card.  Hides entirely when
          count === 0 (matches the "no clutter when caught up" pattern of
          other alerts).  Amber-styled since callbacks are time-sensitive
          Rule 1.4 compliance risk. */}
      {alerts.unreturned_callbacks && alerts.unreturned_callbacks.count > 0 && isWidgetVisible('unreturned_callbacks') && (
        <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-amber-500">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <span>⚠ Unreturned Messages</span>
            <Link to="/communications" className="text-xs text-amber-700 hover:underline ml-auto">Open callback queue →</Link>
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-amber-700">
                {alerts.unreturned_callbacks.count}
              </p>
              <p className="text-xs text-gray-500">
                open {alerts.unreturned_callbacks.count === 1 ? 'callback' : 'callbacks'}
                {alerts.unreturned_callbacks.oldest_days > 0
                  ? ` · oldest ${alerts.unreturned_callbacks.oldest_days} day${alerts.unreturned_callbacks.oldest_days === 1 ? '' : 's'} old`
                  : ''}
              </p>
            </div>
            <p className="text-xs text-gray-500 max-w-xs text-right">
              Client callbacks marked "Needs response" that haven't been returned yet.
              Rule 1.4 compliance — keep clients reasonably informed.
            </p>
          </div>
        </div>
      )}

      {/* Ship 7j (feedback #743) — Phone intakes still awaiting reviewer
          sign-off.  Only rendered for admin OR the designated reviewer; the
          endpoint 403s everyone else so a non-eligible caller silently
          hides the card (no leaked existence).  Amber-accented + hidden
          when count === 0 (matches the caught-up pattern). */}
      {isWidgetVisible('phone_intake_pending') && (
        <PhoneIntakePendingCard />
      )}

      {/* Ledger Balance — renders ONLY when the general ledger does not
          reconcile.  The widget-visibility guard stays HERE with its
          siblings; the data guard lives inside the component.  Hidden when
          clean is the whole point, not a nicety: a card that is permanently
          green becomes wallpaper, and the first real imbalance is the one
          nobody looks at. */}
      {isWidgetVisible('ledger_balance') && (
        <LedgerBalanceCard />
      )}

      {/* Compliance calendar — surfaces CTAPP + State Bar dues + per-attorney
          CLE deadlines.  Fetched independently from /api/compliance/status.
          Kept as its own component so the underlying Dashboard endpoint
          doesn't have to know about compliance concerns. */}
      <ComplianceCalendarCard />
    </div>
  )
}

// Ledger Balance card — the Dashboard half of the reconciliation pulse.
//
// GET /api/accounting/statements/reconciliation-status has computed this
// answer since Ship 7c and had NO caller anywhere: its only reference in the
// tree was its own export in services/api.js.  Nothing watched the ledger, and
// that is precisely why every other accounting defect stayed silent — a wrong
// entry that is internally balanced passes every invariant, every test and
// every reconciliation chip, because the arithmetic is perfect and only the
// ACCOUNTS are wrong.  The nightly accounting-balance-tick cron is the
// authoritative guard (it writes to monitor/alerts.log); this card is the
// same signal where someone is already looking.
//
// Renders NOTHING unless `overall_reconciled === false`.  Three silences, all
// deliberate:
//   * 400 — accounting is not enabled for this firm (today's state).  The
//     flag gate in accounting.js runs ahead of the permission check and
//     answers 400 for anyone who HAS the permission, so an admin gets 400
//     here, not 403.  A card that surfaced that would put a permanent error
//     on the most-visited page of an app the firm has not opted into yet.
//   * 403 — caller lacks can_view_accounting.  Quiet, like every sibling.
//   * any other failure — the cron is the guard that must be loud; a
//     transient API error on the Dashboard is not a ledger finding, and
//     dressing one up as a red integrity banner would be a false alarm.
// The check is `=== false` and not `!overall_reconciled` on purpose: an
// unexpected or truncated payload must fall through to silence rather than
// raise an imbalance alert with no numbers behind it.
function LedgerBalanceCard () {
  const [data, setData] = useState(null)

  useEffect(() => {
    let alive = true
    getReconciliationStatus()
      .then((r) => { if (alive) setData(r.data) })
      .catch(() => { if (alive) setData(null) })
    return () => { alive = false }
  }, [])

  if (!data || data.overall_reconciled !== false) return null

  const bs = data.balance_sheet || {}
  const tb = data.trial_balance || {}
  const basisLabel = data.basis === 'C' ? 'cash' : data.basis === 'A' ? 'accrual' : (data.basis || '')

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-red-600">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span>⚠️ Ledger Does Not Balance</span>
        <Link to="/accounting" className="text-xs text-red-700 hover:underline ml-auto">
          Open Accounting →
        </Link>
      </h3>
      <div className="flex items-start justify-between gap-4">
        <ul className="space-y-1.5 text-xs">
          <ReconCheckRow
            label="Balance Sheet"
            hint="Assets = Liabilities + Equity"
            reconciled={bs.reconciled}
            diffCents={bs.check_diff_cents}
          />
          <ReconCheckRow
            label="Trial Balance"
            hint="Total debits = total credits"
            reconciled={tb.reconciled}
            diffCents={tb.check_diff_cents}
          />
        </ul>
        <p className="text-xs text-gray-500 max-w-xs text-right">
          The general ledger did not reconcile as of{' '}
          {data.as_of ? <span className="font-medium">{data.as_of}</span> : 'today'}
          {basisLabel ? ` (${basisLabel} basis)` : ''}. Review the Statements tab
          before relying on any financial report or closing the period.
        </p>
      </div>
    </div>
  )
}

// One row of the Ledger Balance card.  `reconciled === true` still renders —
// when only one of the two checks fails, seeing that the other one PASSED is
// what tells a bookkeeper where to look first.
function ReconCheckRow ({ label, hint, reconciled, diffCents }) {
  const ok = reconciled === true
  return (
    <li className="flex items-baseline gap-2">
      <span className={ok ? 'text-emerald-600' : 'text-red-600'}>{ok ? '✓' : '✗'}</span>
      <span className="font-medium text-gray-800">{label}</span>
      {ok ? (
        <span className="text-gray-500">balanced</span>
      ) : (
        <span className="text-red-700 font-semibold">off by {signedMoneyFromCents(diffCents)}</span>
      )}
      <span className="text-gray-400">· {hint}</span>
    </li>
  )
}

// Integer cents → a signed dollar string with the sign OUTSIDE the symbol:
// -1234 → "-$12.34", not "$-12.34".  check_diff_cents is always a whole
// number of cents (financialStatements.js computes `reconciled` as
// `checkDiffCents === 0`, exact, with no floating tolerance), so the sign
// carries real information about which side the ledger is short on.
function signedMoneyFromCents (cents) {
  const n = Number(cents || 0) / 100
  return `${n < 0 ? '-' : ''}$${fmt(Math.abs(n))}`
}

// Ship 7j — Phone-intake pending-review card (feedback #743, Cluster C).
// Self-fetching component that renders a compact amber-accented queue for
// the firm's designated reviewer (and admins).  Hidden entirely when:
//   * the endpoint 403s (caller isn't reviewer/admin — quiet fail-soft so
//     staff without permission see nothing rather than an error banner)
//   * count === 0 (caught-up state, matches UnreturnedMessages pattern)
// Also listens for `lead.phone_intake` + `lead.reviewed` realtime events so
// the badge auto-refreshes without a polling loop.  Falls back to a hard
// refresh via router navigation when the reviewer clicks a row (the
// backend `/api/leads/pending-review` endpoint returns the same shape).
function PhoneIntakePendingCard () {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const load = () => {
    api.get('/api/dashboard/phone-intake-pending')
      .then(r => { setData(r.data); setError('') })
      .catch(err => {
        // Silent fail on 403 (caller isn't reviewer/admin) — the card just
        // hides.  Surface other errors internally so the user isn't
        // confused by a phantom empty card.
        if (err.response?.status === 403) { setData(null); setError('') }
        else setError(err.response?.data?.error || 'Failed to load phone-intake queue')
      })
  }
  useEffect(() => {
    load()
    // Realtime refresh: when a new phone intake lands OR a lead is marked
    // reviewed anywhere in the firm, refetch.  Reminder events fire from
    // cron and don't change our count so we skip listening on that.
    const onRealtime = (e) => {
      const t = e?.detail?.type
      if (t === 'lead.phone_intake' || t === 'lead.reviewed') load()
    }
    window.addEventListener('realtime', onRealtime)
    return () => window.removeEventListener('realtime', onRealtime)
  }, [])
  if (error) return null
  if (!data || !data.count || data.count <= 0) return null
  const count = data.count
  const oldest = data.oldest_days || 0
  const recent = Array.isArray(data.recent) ? data.recent.slice(0, 5) : []
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-amber-500">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span>📞 Phone Intakes Awaiting Review</span>
        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5">
          {count}
        </span>
        <Link
          to="/leads?filter=pending_review"
          className="text-xs text-amber-700 hover:underline ml-auto"
        >
          View all →
        </Link>
      </h3>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-3xl font-bold text-amber-700">{count}</p>
          <p className="text-xs text-gray-500">
            {count === 1 ? 'lead' : 'leads'} pending review
            {oldest > 0 ? ` · oldest ${oldest} day${oldest === 1 ? '' : 's'} old` : ''}
          </p>
        </div>
        <p className="text-xs text-gray-500 max-w-xs text-right">
          Phone-intake leads awaiting your review.  Daily reminders at
          the configured hour until each is marked reviewed.
        </p>
      </div>
      {recent.length > 0 && (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {recent.map((r) => {
            const name = [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || `Lead #${r.id}`
            const age = Number.isFinite(r.age_days) ? r.age_days
              : (r.created_at ? Math.max(0, Math.round((Date.now() - new Date(r.created_at).getTime()) / 86400000)) : null)
            return (
              <li key={r.id} className="py-1.5">
                <Link
                  to={`/leads?filter=pending_review&highlight=${r.id}`}
                  className="flex items-center justify-between text-xs hover:bg-amber-50 rounded px-1 py-0.5 -mx-1 transition"
                >
                  <span className="font-medium text-gray-800">{name}</span>
                  <span className="text-gray-500">
                    {age !== null ? `${age} day${age === 1 ? '' : 's'} old` : (r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true }) : '')}
                  </span>
                </Link>
                {/* Feedback #841 (b) — questionnaire follow-up label from the
                    API (lib/questionnaire.js).  "Not returned" means Shawn can
                    decline with a non-engagement letter instead of reviewing. */}
                {followupLabel(r.questionnaire_followup) && (
                  <div className={`mt-0.5 inline-block text-[11px] rounded-full px-2 py-px ${followupChipClass(r.questionnaire_followup)}`}>
                    {followupIcon(r.questionnaire_followup)} {followupLabel(r.questionnaire_followup)}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Compliance calendar card — CTAPP + State Bar dues + per-attorney CLE.
// Fetches /api/compliance/status independently.  Renders a compact 3-column
// summary + a per-attorney CLE list if any exist.  Hides entirely when
// there are zero imminent items + zero overdue (caught-up state).
function ComplianceCalendarCard () {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  useEffect(() => {
    api.get('/api/compliance/status')
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load compliance status'))
  }, [])
  if (error || !data) return null
  const { ctapp, state_bar, cle, summary } = data
  // Only render when something is imminent / overdue.  Zero-alert state
  // means the firm is caught up — Dashboard already de-clutters.
  if (summary.imminent_count === 0 && summary.overdue_count === 0) return null
  const daysColor = (d, threshold = 30) => {
    if (d < 0) return 'text-red-700 bg-red-50'
    if (d <= threshold) return 'text-yellow-700 bg-yellow-50'
    return 'text-gray-600 bg-gray-50'
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-amber-500">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span>📅 Compliance Calendar</span>
        <span className="text-xs text-gray-400">bar-hygiene deadlines</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-3">
        {/* CTAPP */}
        <div className={`rounded-lg p-3 ${daysColor(ctapp.days_until)}`}>
          <p className="text-xs font-medium text-gray-600 uppercase">CTAPP {ctapp.filing_year}</p>
          <p className="text-lg font-bold">
            {ctapp.needs_filing ? `${ctapp.days_until} days` : '✓ Filed'}
          </p>
          <p className="text-xs opacity-75">
            {ctapp.needs_filing
              ? `Due ${new Date(ctapp.next_due + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
              : `on ${new Date(ctapp.filed_at).toLocaleDateString()}`}
          </p>
        </div>
        {/* State Bar dues */}
        <div className={`rounded-lg p-3 ${daysColor(state_bar.days_until, 45)}`}>
          <p className="text-xs font-medium text-gray-600 uppercase">State Bar Dues</p>
          <p className="text-lg font-bold">{state_bar.days_until} days</p>
          <p className="text-xs opacity-75">
            Due {new Date(state_bar.next_due + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        {/* CLE rollup */}
        <div className={`rounded-lg p-3 ${cle.some(c => c.days_until <= 90) ? (cle.some(c => c.days_until < 0) ? 'text-red-700 bg-red-50' : 'text-yellow-700 bg-yellow-50') : 'text-gray-600 bg-gray-50'}`}>
          <p className="text-xs font-medium text-gray-600 uppercase">CLE Deadlines</p>
          <p className="text-lg font-bold">
            {cle.length === 0 ? 'None set' : `${cle.filter(c => c.days_until <= 90).length} of ${cle.length}`}
          </p>
          <p className="text-xs opacity-75">
            {cle.length === 0 ? 'Set per attorney on Team page' : 'imminent (≤ 90 days)'}
          </p>
        </div>
      </div>
      {cle.some(c => c.days_until <= 90) && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Attorneys with imminent CLE deadlines</p>
          <ul className="space-y-1">
            {cle.filter(c => c.days_until <= 90).map(c => (
              <li key={c.user_id} className="text-sm flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.days_until < 0 ? 'bg-red-100 text-red-700' : c.days_until <= 30 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                  {c.days_until < 0 ? `${Math.abs(c.days_until)}d overdue` : `${c.days_until}d`}
                </span>
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-gray-500">
                  due {new Date(c.cle_compliance_date + 'T00:00:00').toLocaleDateString()}
                  {c.notes && ` · ${c.notes}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// Batch D-D.2 — AR & Collections Health KPI card (widget id `financial_health`).
// PROP-DRIVEN on purpose.  The payload arrives inside the single /api/dashboard
// response, which the backend already gates behind the `invoices` perm.  Do NOT
// convert this to self-fetching the way PhoneIntakePendingCard and
// SecurityAlertsCard do: that would add an HTTP round-trip this page does not
// currently make, and would need a permission gate of its own.
//
// BOTH render guards — payload present AND widget not hidden — stay at the CALL
// SITE, where every sibling card keeps them.  Guarded by
// frontend/src/pages/Dashboard.financialHealthCard.test.js.
function FinancialHealthCard ({ data }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-indigo-500">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span>💹 AR &amp; Collections Health</span>
        <Link to="/reports" className="text-xs text-indigo-600 hover:underline ml-auto">Open Cash Flow reports →</Link>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        {/* Stale WIP */}
        <div className={`rounded-lg p-3 ${data.stale_wip.amount > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}
             title={`WIP older than 60 days across ${data.stale_wip.matter_count} matter${data.stale_wip.matter_count === 1 ? '' : 's'}. This is unbilled work at high risk of write-off.`}>
          <p className="text-xs font-medium text-gray-600 uppercase">Stale WIP (&gt; 60 days)</p>
          <p className={`text-xl font-bold ${data.stale_wip.amount > 0 ? 'text-amber-700' : 'text-gray-600'}`}>
            {data.stale_wip.amount > 0
              ? `$${Math.round(data.stale_wip.amount).toLocaleString()}`
              : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {data.stale_wip.matter_count > 0
              ? `${data.stale_wip.matter_count} matter${data.stale_wip.matter_count === 1 ? '' : 's'} · oldest ${data.stale_wip.oldest_days}d`
              : 'nothing older than 60d'}
          </p>
        </div>
        {/* DSO trend */}
        <div className={`rounded-lg p-3 ${data.dso.current_days > 60 ? 'bg-amber-50' : 'bg-gray-50'}`}
             title="Days-Sales-Outstanding — average days from invoice sent to first payment received.  Lower is better.">
          <p className="text-xs font-medium text-gray-600 uppercase">Avg days to collect</p>
          <p className={`text-xl font-bold ${data.dso.current_days > 60 ? 'text-amber-700' : 'text-gray-700'}`}>
            {data.dso.current_days > 0
              ? `${Math.round(data.dso.current_days)}d`
              : '—'}
          </p>
          {data.dso.prior_days > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">
              {data.dso.delta_days < 0
                ? <span className="text-emerald-700">↓ {Math.abs(Math.round(data.dso.delta_days))}d vs prior 90d</span>
                : data.dso.delta_days > 0
                  ? <span className="text-red-600">↑ {Math.round(data.dso.delta_days)}d vs prior 90d</span>
                  : <span>≈ prior 90d</span>}
            </p>
          )}
        </div>
        {/* Expected 30d collections */}
        <div className="rounded-lg p-3 bg-emerald-50"
             title="Expected cash into the operating account over the next 30 days, based on current AR × historical recovery rates by age bucket.">
          <p className="text-xs font-medium text-gray-600 uppercase">Expected 30d cash</p>
          <p className="text-xl font-bold text-emerald-700">
            {data.expected_30d_collections > 0
              ? `$${Math.round(data.expected_30d_collections).toLocaleString()}`
              : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">forecast based on aging</p>
        </div>
      </div>
    </div>
  )
}

// True Profit This Month card (widget id `true_profit_mtd`).
//
// THE LOAD-BEARING PART IS THE REFUSAL.  "Billed − hard costs" is the naive
// number Batch D-D.4 shipped the attorney cost basis to replace.  When no time
// entry in the period resolves a cost rate, attorney_cost comes back as $0 and
// the figure is that naive number EXACTLY — but printed under a label that
// says "true".  A reader cannot tell the difference by looking, because the
// missing input renders as a plausible zero rather than as a gap.  So at
// `basis === 'no_cost_rates'` this card shows NO dollar figure at all; it
// shows what is actually wrong and where to fix it.
//
// This is not a hypothetical edge case.  Measured on production 2026-09-16:
// all 11 attorney_cost_rate_history rows sit at cost_rate = 0 (the D-D.4
// migration seeded them that way on purpose) and no user has a rate, so
// zero-coverage is the state this card ships into on day one.
//
// Missing BILLED revenue is handled differently and the asymmetry is
// deliberate: billed_fees = $0 is visible on the card itself, so a caveat is
// honest there, whereas a $0 cost is invisible.  Until the #837 Amicus repair,
// no time entry had ever carried status='billed' (the repair marks Amicus
// history billed, mostly at no charge), which is why the "nothing invoiced
// yet" note exists rather than being dead code — a firm with no billed time
// this month still hits it.
//
// PROP-DRIVEN like FinancialHealthCard: the payload rides the single
// /api/dashboard response the page already makes.  Guarded by
// frontend/src/pages/Dashboard.trueProfitCard.test.js.
function TrueProfitCard ({ data }) {
  // Sign goes OUTSIDE the currency symbol: Math.round(-1000).toLocaleString()
  // yields "$-1,000", which reads as a typo on a figure a partner scans.  This
  // is not a rare branch — production has cost rates unset and nothing marked
  // billed, so the first month after rates are configured is billed $0 minus
  // real attorney cost, i.e. negative.
  const money = (n) => {
    const v = Math.round(Number(n) || 0)
    return `${v < 0 ? '\u2212' : ''}$${Math.abs(v).toLocaleString()}`
  }
  const heading = (
    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
      <span>📈 True Profit This Month</span>
      <Link to="/reports" className="text-xs text-indigo-600 hover:underline ml-auto">Open True Matter P&amp;L →</Link>
    </h3>
  )

  // No billable work and no posted costs — "configure cost rates" would be the
  // wrong prompt, because there is nothing to cost in the first place.
  if (data.basis === 'no_activity') {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-gray-300">
        {heading}
        <p className="text-sm text-gray-500">No billable activity recorded yet this month.</p>
      </div>
    )
  }

  // THE REFUSAL.  No dollar figure is rendered anywhere in this branch.
  if (data.basis !== 'ok') {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-amber-500">
        {heading}
        <p className="text-sm font-medium text-amber-800">
          Attorney cost rates are not configured, so true profit cannot be calculated.
        </p>
        <p className="text-xs text-gray-600 mt-1">
          {data.unset_entries} billable time {data.unset_entries === 1 ? 'entry' : 'entries'} this
          month {data.unset_entries === 1 ? 'has' : 'have'} no cost rate on file. Without one, the
          attorney-time cost of the work counts as zero, and any profit figure would simply be
          revenue minus hard costs. Deliberately worded without a currency amount: this branch
          renders no figure at all, and the test asserts that by looking for any `$` in the card.
        </p>
        <Link to="/team" className="inline-block mt-2 text-xs font-medium text-amber-700 hover:underline">
          Set cost rates in Team → Cost Rates →
        </Link>
      </div>
    )
  }

  const profit = Number(data.true_profit) || 0
  return (
    <div className={`bg-white rounded-2xl shadow-sm p-5 border-l-4 ${profit >= 0 ? 'border-emerald-500' : 'border-red-500'}`}>
      {heading}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="rounded-lg p-3 bg-gray-50">
          <p className="text-xs font-medium text-gray-600 uppercase">Billed fees</p>
          <p className="text-lg font-bold text-gray-700">{money(data.billed_fees)}</p>
        </div>
        <div className="rounded-lg p-3 bg-gray-50">
          <p className="text-xs font-medium text-gray-600 uppercase">Hard costs</p>
          <p className="text-lg font-bold text-gray-700">{money(data.hard_costs)}</p>
        </div>
        <div className="rounded-lg p-3 bg-gray-50"
             title="Fully-loaded attorney time cost, resolved per entry from the rate effective on that entry's date.">
          <p className="text-xs font-medium text-gray-600 uppercase">Attorney cost</p>
          <p className="text-lg font-bold text-gray-700">{money(data.attorney_cost)}</p>
        </div>
        <div className={`rounded-lg p-3 ${profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <p className="text-xs font-medium text-gray-600 uppercase">True profit</p>
          <p className={`text-lg font-bold ${profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
            {money(profit)}
          </p>
          {typeof data.margin_pct === 'number' && (
            <p className="text-xs text-gray-500 mt-0.5">{data.margin_pct}% margin</p>
          )}
        </div>
      </div>

      {/* Partial coverage — show the figure, but name what it is missing.
          Same wording shape as the matter_true_pnl banner on Reports so the
          two surfaces read consistently. */}
      {data.unset_entries > 0 && (
        <p className="text-xs text-amber-700 mt-3">
          ⚠ {data.unset_entries} billable {data.unset_entries === 1 ? 'entry' : 'entries'} this month
          {data.unset_entries === 1 ? ' has' : ' have'} no attorney cost rate and {data.unset_entries === 1 ? 'is' : 'are'} counted
          at $0 cost — true profit is overstated by that amount.
        </p>
      )}
      {data.billed_fees === 0 && (
        <p className="text-xs text-gray-500 mt-2">
          Nothing has been invoiced this month, so this figure is cost only — it is not a loss.
        </p>
      )}
    </div>
  )
}

function fmt(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function MetricCard({ icon, title, value, subvalue, trend, change, color, link }) {
  const Arrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'
  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-400'
  const inner = (
    <div className={`p-5 rounded-2xl shadow-sm border border-gray-200 ${color}`}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-75 mb-1">{icon} {title}</p>
      <p className="text-2xl font-bold">{value}</p>
      {subvalue && <p className="text-sm mt-1 opacity-75">{subvalue}</p>}
      {typeof change === 'number' && (
        <p className={`text-xs mt-2 ${trendColor}`}>
          {Arrow} {Math.abs(change).toFixed(1)}% vs last month
        </p>
      )}
    </div>
  )
  return link ? <Link to={link}>{inner}</Link> : inner
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 p-3 rounded-lg bg-gray-50 hover:bg-primary-50 hover:text-primary-700 border border-gray-200 transition-colors text-gray-700 text-xs font-medium">
      <span className="text-2xl">{icon}</span>
      {label}
    </button>
  )
}

function AlertCard({ title, items, to, render, emptyMessage }) {
  // An alert list the payload omits renders as empty, not a crash.
  const list = Array.isArray(items) ? items : []
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <Link to={to} className="text-xs text-primary-600 hover:underline">View all</Link>
      </div>
      <div className="divide-y divide-gray-100">
        {list.length === 0 && <p className="p-5 text-sm text-gray-400">{emptyMessage}</p>}
        {list.slice(0, 3).map((item, i) => (
          <div key={i} className="px-5 py-3">{render(item)}</div>
        ))}
      </div>
    </div>
  )
}

function PriorityDot({ p }) {
  const map = { urgent: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-400', low: 'bg-gray-300' }
  return <span className={`inline-block w-2 h-2 rounded-full mt-2 ${map[p] || 'bg-primary-400'}`} />
}

function HealthDot() {
  const [state, setState] = useState({ color: 'gray', label: 'Checking…' })
  const [showPanel, setShowPanel] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      const start = Date.now()
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 8000)
      try {
        const baseURL = api.defaults.baseURL || ''
        const res = await fetch(`${baseURL}/api/health`, { cache: 'no-store', signal: controller.signal })
        clearTimeout(timer)
        const dur = Date.now() - start
        if (cancelled) return
        if (res.ok) {
          const data = await res.json().catch(() => ({}))
          const slow = dur >= 500
          setState({ color: slow ? 'yellow' : 'green',
            label: `API ${slow ? 'slow' : 'healthy'} — ${dur}ms${data.database ? ` · DB: ${data.database}` : ''}${data.uptime ? ` · Up: ${Math.round(data.uptime / 60)}m` : ''}` })
        } else {
          setState({ color: 'red', label: `API ${res.status} — ${dur}ms` })
        }
      } catch (err) {
        clearTimeout(timer)
        if (cancelled) return
        const aborted = err.name === 'AbortError'
        setState({ color: 'red', label: aborted ? 'API timeout (>8s)' : `API unreachable — ${err.message}` })
      }
    }
    check()
    const id = setInterval(check, 60000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const loadDetail = async () => {
    setDetailLoading(true)
    try {
      const res = await api.get('/api/health/detailed')
      setDetail(res.data)
    } catch (err) {
      setDetail({ error: err.response?.data?.error || err.message })
    } finally {
      setDetailLoading(false)
    }
  }

  const openPanel = () => {
    setShowPanel(true)
    loadDetail()
  }

  const cls = { green: 'bg-green-500', yellow: 'bg-yellow-400', red: 'bg-red-500 animate-pulse', gray: 'bg-gray-300' }

  return (
    <>
      <button
        onClick={openPanel}
        title={`${state.label} · click for details`}
        aria-label={state.label}
        className="inline-flex items-center">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls[state.color]}`} />
      </button>
      {showPanel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setShowPanel(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">🩺 System Health</h3>
              <button onClick={() => setShowPanel(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {detailLoading && <p className="text-gray-500">Loading detailed health…</p>}
              {detail?.error && <p className="text-red-600">{detail.error}</p>}
              {detail && !detail.error && (
                <>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${detail.status === 'ok' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                    <span className="font-medium">{detail.status === 'ok' ? 'All systems operational' : 'Degraded'}</span>
                    <span className="text-gray-400 text-xs ml-auto">v{detail.version} · up {Math.round(detail.uptime_sec / 60)} min</span>
                  </div>
                  {Object.entries(detail.checks || {}).map(([key, val]) => (
                    <div key={key} className={`rounded-lg border p-3 ${val.ok ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                      <div className="flex items-start justify-between">
                        <div className="font-medium text-gray-800 capitalize">{key.replace(/_/g, ' ')}</div>
                        <span className={val.ok ? 'text-green-700 text-xs' : 'text-yellow-700 text-xs'}>{val.ok ? '✓ OK' : '⚠️ Degraded'}</span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1 font-mono">
                        {Object.entries(val).filter(([k]) => k !== 'ok').map(([k, v]) => (
                          <div key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Suspicious activity detection — admin-only card that surfaces rule-based
// alerts computed on-demand from user_login_history, user_sessions, and
// audit_log EXPORT rows.  Hidden entirely when zero alerts fire (clean =
// no clutter).
// Rule descriptions surface on the Security Alerts card as (a) native title
// tooltips on the rule-label chips, and (b) an "ℹ️ About these rules"
// expandable legend at the top of the card.  Thresholds mirror api/src/routes/
// securityAlerts.js CFG — keep in sync if backend defaults change.  Per feedback
// #258 (Shawn) — the terse chip labels weren't self-explanatory.
const RULE_INFO = {
  bulk_export: {
    what: 'A user downloaded or exported an unusually large number of files in a short window.',
    threshold: '≥ 20 exports within 10 minutes for the same user.',
    response: 'Could be a legit month-end pull, or a departing employee grabbing data.  Check the user + click through to Audit Log to see what was exported.',
  },
  off_hours_login: {
    what: 'A user signed in during the firm’s off-hours window.',
    threshold: '22:00 – 06:00 in the firm’s configured timezone.',
    response: 'Not always suspicious (some attorneys work nights) — but a NEW off-hours login for someone who’s always been 9-to-5 is worth a look.',
  },
  new_device: {
    what: 'A user signed in from a device + network combination we’ve never seen before.',
    threshold: 'First seen within the last 24 hours.',
    response: 'Complements the new-device email notification.  If the user didn’t sign in from a new device, treat as compromised — reset password + revoke sessions.',
  },
  multi_ip: {
    what: 'A user signed in from an unusually high number of distinct networks in one day.',
    threshold: '≥ 3 distinct IP /24 (or /64 for IPv6) prefixes within 24 hours.',
    response: 'Home + office + phone LTE = 3, so this triggers only above that.  Impossible-travel proxy — attorneys don’t normally hop 4+ networks/day.',
  },
  session_spike: {
    what: 'A user has an unusually high number of active (non-revoked, non-expired) sessions.',
    threshold: '> 5 concurrent active sessions for one user.',
    response: 'Usually credential sharing (dangerous — every session is another attack surface).  Ask the user, revoke sessions they don’t recognize.',
  },
}

function SecurityAlertsCard() {
  const [data, setData] = useState(null)
  const [breakGlass, setBreakGlass] = useState(null)
  const [loading, setLoading] = useState(true)
  // Never set: both fetches below catch their own failures and resolve null.
  const [error] = useState('')
  const [collapsed, setCollapsed] = useState(false)
  const [showLegend, setShowLegend] = useState(false)

  useEffect(() => {
    // Load both in parallel — the rule-based alerts + the break-glass
    // event feed.  Failures on either are tolerated (each render branch
    // handles null).
    Promise.all([
      api.get('/api/security-alerts').then(r => r.data).catch(() => null),
      api.get('/api/security-alerts/break-glass?days=7').then(r => r.data).catch(() => null),
    ]).then(([alerts, bg]) => {
      setData(alerts)
      setBreakGlass(bg)
    }).finally(() => setLoading(false))
  }, [])

  const alertTotal = data?.total || 0
  const breakGlassCount = breakGlass?.count || 0

  // Hide entirely when there's nothing to alert on AND no break-glass
  // events in the last 7 days — matches the "no clutter when caught up"
  // pattern of other Dashboard alerts.
  if (loading || error || (alertTotal === 0 && breakGlassCount === 0)) return null

  const sevColor = {
    high:   'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low:    'bg-blue-100 text-blue-700 border-blue-200',
  }
  const ruleLabel = {
    bulk_export:      '📦 Bulk export burst',
    off_hours_login:  '🌙 Off-hours login',
    new_device:       '🆕 New device',
    multi_ip:         '🌍 Multiple networks',
    session_spike:    '⚠️ Many active sessions',
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">🚨 Security Alerts</h3>
          {data?.counts?.high > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
              {data.counts.high} high
            </span>
          )}
          {data?.counts?.medium > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              {data.counts.medium} medium
            </span>
          )}
          {data?.counts?.low > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
              {data.counts.low} low
            </span>
          )}
          {breakGlassCount > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
              🔒 {breakGlassCount} break-glass
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLegend(s => !s)}
            className="text-xs text-gray-500 hover:text-primary-700"
            title="Explain what each rule detects and what to do about it"
            aria-expanded={showLegend}>
            {showLegend ? 'Hide info' : 'ℹ️ About these rules'}
          </button>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="text-xs text-gray-500 hover:text-gray-700">
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>
      {showLegend && !collapsed && (
        <div className="mb-3 -mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-2">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">Rule legend</div>
          {Object.entries(RULE_INFO).map(([key, info]) => (
            <div key={key} className="flex items-start gap-2">
              <span className="shrink-0 font-medium text-gray-700 min-w-[9.5rem]">{ruleLabel[key] || key}</span>
              <div className="min-w-0 flex-1 text-gray-600">
                <div>{info.what}</div>
                <div className="mt-0.5"><span className="text-gray-500">Trigger:</span> {info.threshold}</div>
                <div className="mt-0.5"><span className="text-gray-500">What to do:</span> {info.response}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!collapsed && (
        <>
          {alertTotal > 0 && data && (
            <div className="divide-y divide-gray-100">
              {data.alerts.slice(0, 8).map(a => {
                const info = RULE_INFO[a.rule]
                const tooltip = info
                  ? `${info.what}\n\nTrigger: ${info.threshold}\n\nWhat to do: ${info.response}`
                  : undefined
                return (
                  <div key={a.id} className="py-2 flex items-start gap-3">
                    <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${sevColor[a.severity] || sevColor.low}`}>
                      {a.severity}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-800">
                        <span
                          className="text-gray-500 mr-2 cursor-help border-b border-dashed border-gray-300"
                          title={tooltip}>
                          {ruleLabel[a.rule] || a.rule}
                        </span>
                        {a.summary}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{a.detail}</div>
                    </div>
                  </div>
                )
              })}
              {data.alerts.length > 8 && (
                <div className="pt-2 text-xs text-gray-500">
                  …and {data.alerts.length - 8} more.  See <Link to="/audit-log" className="text-primary-600 hover:underline">Audit Log</Link> for full history.
                </div>
              )}
            </div>
          )}

          {breakGlassCount > 0 && breakGlass && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-xs font-semibold uppercase tracking-wide text-purple-700 mb-2">🔒 Break-Glass Events (last 7 days)</div>
              <div className="divide-y divide-gray-100">
                {breakGlass.events.slice(0, 5).map(e => (
                  <div key={e.id} className="py-1.5 text-xs">
                    <div className="text-gray-800">
                      <span className="font-medium">{e.actor_name || 'Admin'}</span>
                      <span className="text-gray-500 mx-1">→</span>
                      <span className="font-medium">{e.action.replace(/_/g, ' ').toLowerCase()}</span>
                      <span className="text-gray-500 mx-1">on</span>
                      <span className="font-medium">{e.target_name || `user #${e.target_user_id || '?'}`}</span>
                      <span className="text-gray-400 ml-2">· {new Date(e.when).toLocaleString()}</span>
                    </div>
                    {e.reason && (
                      <div className="text-gray-500 mt-0.5 pl-4 italic">"{e.reason}"</div>
                    )}
                    {!e.reason && (
                      <div className="text-gray-400 mt-0.5 pl-4 italic">no reason provided</div>
                    )}
                  </div>
                ))}
                {breakGlass.events.length > 5 && (
                  <div className="pt-1.5 text-xs text-gray-500">
                    …and {breakGlass.events.length - 5} more.  See <Link to="/audit-log" className="text-primary-600 hover:underline">Audit Log</Link> for full history.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      <div className="mt-3 text-[10px] text-gray-400">
        Rules: bulk-export burst · off-hours login · new device · multi-network · session spike · break-glass admin actions.  Firm timezone: {data?.firm_timezone || '—'}.
      </div>
    </div>
  )
}
